import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Snowflake, ShieldCheck, Sparkle, WarningCircle, GlobeHemisphereWest, Copy } from "@phosphor-icons/react";
import { Toaster, toast } from "sonner";
import Snowflakes from "@/components/Snowflakes";

export default function Login() {
  const { login, formatApiErrorDetail } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState(() => localStorage.getItem("prefill_email") || "");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needTotp, setNeedTotp] = useState(false);
  const [loading, setLoading] = useState(false);
  // Inline error banner state — shown right under the form so the user
  // never misses it (toasts on /login used to fail because <Toaster/> only
  // lived inside AppShell).
  const [errBanner, setErrBanner] = useState(null); // { kind: 'ip' | 'generic', message, ip? }

  // Clear prefill after first render so it doesn't persist forever
  useState(() => { localStorage.removeItem("prefill_email"); });

  const parseError = (err) => {
    const status = err.response?.status;
    const rawDetail = err.response?.data?.detail;
    // If backend returned a detail body → use it. Otherwise surface the real
    // network-level error (axios `err.message`, e.g. "Network Error",
    // "timeout of 30000ms exceeded") instead of the generic fallback.
    const detail = rawDetail != null
      ? formatApiErrorDetail(rawDetail)
      : (err.message ? `${err.message}${status ? ` (HTTP ${status})` : ""}` : "Login failed");
    // Backend sends "IP 1.2.3.4 is not whitelisted. Contact admin."
    const ipMatch = /IP\s+([0-9a-fA-F:.]+)\s+is not whitelisted/i.exec(detail);
    if (status === 403 && ipMatch) {
      return { kind: "ip", ip: ipMatch[1], message: detail };
    }
    if (status === 429) return { kind: "generic", message: `${detail} (rate limited)` };
    if (status === 401) return { kind: "generic", message: detail };
    if (status === 403) return { kind: "generic", message: detail };
    // No status = network/CORS/DNS failure — spell it out
    if (!status) return { kind: "generic", message: `${detail} — the app couldn't reach the server. Check your connection or try again.` };
    return { kind: "generic", message: `${detail} (HTTP ${status})` };
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrBanner(null);
    try {
      const r = await login({ email, password, totp_code: totp || undefined });
      if (r.requires_totp) { setNeedTotp(true); toast.info("2FA code required"); return; }
      if (r.requires_totp_setup) {
        toast.success("Signed in — 2FA setup required");
        nav("/setup-2fa");
        return;
      }
      toast.success("Welcome back");
      nav("/");
    } catch (ex) {
      const parsed = parseError(ex);
      setErrBanner(parsed);
      toast.error(parsed.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <Snowflakes count={60} />

      {/* animated soft blobs */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full opacity-70"
             style={{ background: "radial-gradient(closest-side, rgba(245, 200, 66, 0.75), transparent)" }} />
        <div className="absolute -bottom-32 -right-24 w-[600px] h-[600px] rounded-full opacity-70"
             style={{ background: "radial-gradient(closest-side, rgba(200,205,220,0.85), transparent)" }} />
        <div className="absolute top-1/3 right-1/3 w-[300px] h-[300px] rounded-full opacity-40"
             style={{ background: "radial-gradient(closest-side, rgba(232, 184, 74, 0.7), transparent)" }} />
      </div>

      <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
        {/* Left showcase */}
        <div className="hidden lg:flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl chrome flex items-center justify-center shimmer-border">
              <Snowflake size={22} weight="fill" className="text-amber-500" />
            </div>
            <div>
              <div className="font-heading font-black text-2xl leading-none bg-gradient-to-br from-amber-500 to-slate-500 bg-clip-text text-transparent">PayTrack</div>
              <div className="overline mt-1">Payment operations</div>
            </div>
          </div>

          <div className="space-y-6 max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-xs font-medium text-amber-700">
              <Sparkle size={12} weight="fill" /> Champagne Gold
            </div>
            <h1 className="font-heading text-5xl xl:text-6xl font-black tracking-tighter leading-[1.05]">
              Every payment.<br />
              <span className="bg-gradient-to-br from-amber-500 via-amber-400 to-slate-500 bg-clip-text text-transparent">Requested. Audited. Verified.</span>
            </h1>
            <p className="text-sm text-slate-600 max-w-md">Track vendor payments across brands and currencies — fiat and crypto — with on-chain verification and a full audit trail.</p>

            <div className="grid grid-cols-3 gap-4 pt-6">
              <Stat label="Roles" value="4" />
              <Stat label="Chains" value="5+" />
              <Stat label="Currencies" value="8" />
            </div>
          </div>

          <div className="overline text-slate-500">v1.0 · internal</div>
        </div>

        {/* Right form card */}
        <div className="flex items-center justify-center px-4 sm:px-6 lg:px-12 py-10">
          <div className="w-full max-w-md glass-strong rounded-3xl p-6 sm:p-10 shimmer-border">
            <div className="overline mb-4">Sign in</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mb-2">Welcome back</h2>
            <p className="text-sm text-slate-600 mb-8">Use your PayTrack credentials to continue.</p>

            {errBanner && errBanner.kind === "ip" && (
              <div
                data-testid="login-error-ip"
                className="mb-5 rounded-2xl border border-amber-300 bg-amber-50/90 p-4 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <GlobeHemisphereWest size={20} weight="duotone" className="text-amber-700 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold text-amber-900">Your IP isn&apos;t whitelisted</div>
                    <div className="text-xs text-slate-700 mt-1 leading-relaxed">
                      Ask an admin to whitelist this IP address in <span className="font-semibold">Admin → Security</span>:
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <code
                        data-testid="login-error-ip-value"
                        className="text-sm font-mono font-bold bg-white/80 border border-amber-200 rounded-lg px-3 py-1.5 text-amber-900 break-all"
                      >
                        {errBanner.ip}
                      </code>
                      <button
                        type="button"
                        data-testid="login-error-ip-copy"
                        onClick={() => {
                          navigator.clipboard.writeText(errBanner.ip);
                          toast.success("IP copied");
                        }}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 hover:text-amber-950 px-2 py-1 rounded-lg hover:bg-white/60"
                      >
                        <Copy size={12} weight="bold" /> Copy
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {errBanner && errBanner.kind === "generic" && (
              <div
                data-testid="login-error-generic"
                role="alert"
                className="mb-5 rounded-2xl border border-red-300 bg-red-50/90 p-4 shadow-sm flex items-start gap-3"
              >
                <WarningCircle size={20} weight="duotone" className="text-red-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-red-800">Couldn&apos;t sign in</div>
                  <div className="text-xs text-slate-700 mt-1 leading-relaxed break-words">{errBanner.message}</div>
                </div>
              </div>
            )}

            <form onSubmit={submit} className="space-y-5" data-testid="login-form">
              <div>
                <Label htmlFor="email" className="text-xs font-semibold text-slate-700 tracking-wide">EMAIL</Label>
                <Input id="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)}
                       type="email" autoComplete="email" required
                       className="mt-1.5 h-11 rounded-xl bg-white/70 border-white/60 backdrop-blur focus-visible:ring-amber-400" />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs font-semibold text-slate-700 tracking-wide">PASSWORD</Label>
                <Input id="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)}
                       type="password" autoComplete="current-password" required
                       className="mt-1.5 h-11 rounded-xl bg-white/70 border-white/60 backdrop-blur focus-visible:ring-amber-400" />
              </div>
              {needTotp && (
                <div>
                  <Label htmlFor="totp" className="flex items-center gap-2 text-xs font-semibold text-slate-700 tracking-wide">
                    <ShieldCheck size={14} weight="fill" /> AUTHENTICATOR CODE
                  </Label>
                  <Input id="totp" data-testid="login-totp" value={totp} onChange={(e) => setTotp(e.target.value)}
                         placeholder="123456" inputMode="numeric" maxLength={6} required
                         className="mt-1.5 h-11 rounded-xl bg-white/70 border-white/60 backdrop-blur font-mono text-center tracking-widest" />
                </div>
              )}
              <button type="submit" data-testid="login-submit" disabled={loading}
                      className="amber-pill w-full h-12 rounded-xl font-semibold text-sm tracking-wide">
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </div>
        </div>
      </div>
      <Toaster richColors position="top-right" closeButton />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="glass rounded-2xl p-4">
      <div className="font-heading font-black text-3xl bg-gradient-to-br from-amber-500 to-slate-600 bg-clip-text text-transparent">{value}</div>
      <div className="overline mt-1">{label}</div>
    </div>
  );
}
